import { POSITION_COMPLEMENTS, POSITION_SUBSTITUTES } from './dimensions';
import { getElementBoundingBox } from './utils';
import getScreenCoordinates from './getScreenCoordinates';

/**
 * Calculates the absolute positioning (relative to container) to place a popover element
 *
 * @param align {string} Cross-axis alignment. One of ['bottom', 'left', 'right', 'top']
 * @param [allowCrossAxis = true] {boolean} Whether to allow the popover to be positioned on the cross axis
 * @param anchor {HTMLElement} Element to position the popover too
 * @param [arrowConfig] {{ arrowBuffer: number, arrowWidth: number }} If present describes the size & constraints for an arrow element
 * @param [buffer=16] {number} Minimum distance between the popover and the anchor
 * @param [container] {HTMLElement} Element the popover must be constrained to fit within
 * @param [forcePosition] {boolean} If true, use only the provided position value and don't try any other position
 * @param [offset=0] {number} Distance between the popover and the anchor
 * @param popover {HTMLElement} Element containing the popover content
 * @param position {string} Position the user wants. One of ['bottom', 'left', 'right', 'top']
 *
 * @returns({
 *  arrow?: { left: number, top: number },
 *  fit: number,
 *  left: number,
 *  position: string,
 *  top: number
 * } | null) absolute page coordinates for the popover, and the placement's relation to the anchor. If there's no room this returns null
 */

const findPopoverPosition = ({
  align,
  allowCrossAxis = true,
  anchor,
  arrowConfig,
  buffer = 16,
  container,
  forcePosition,
  offset = 0,
  popover,
  position,
}) => {
  // Find the screen-relative bounding boxes of the
  // anchor, container, and popover
  const anchorBoundingBox = getElementBoundingBox(anchor);
  const popoverBoundingBox = getElementBoundingBox(popover);

  const documentWidth =
    document.documentElement.clientWidth || window.innerWidth;
  const documentHeight =
    document.documentElement.clientHeight || window.innerHeight;

  const windowBoundingBox = {
    bottom: documentHeight,
    height: documentHeight,
    left: 0,
    right: documentWidth,
    top: 0,
    width: documentWidth,
  };

  // If no container element is passed, fallback to using the window viewport
  const containerBoundingBox = container
    ? getElementBoundingBox(container)
    : windowBoundingBox;

  /**
   *  position was specified by the function caller and is a strong hint
   *  as to the preferred location of the popover relative to the anchor.
   *  However, we strongly prefer showing all of the popover content within
   *  the window + container boundary and will iterate ocver the four
   *  possible sides until a perfect fit is located
   *  If none of the locations fully contain popover, the location with the
   *  best fit is selected
   */

  const iterationPositions = [position];
  const iterationAlignments = [align];

  if (forcePosition !== true) {
    iterationAlignments.push(align);
    iterationPositions.push(POSITION_COMPLEMENTS[position]);
    if (allowCrossAxis) {
      iterationPositions.push(
        POSITION_SUBSTITUTES[position],
        POSITION_COMPLEMENTS[POSITION_SUBSTITUTES[position]]
      );
      iterationAlignments.push(undefined, undefined);
    }
  } else {
    // Position is forced, if it conflicts with the alignment then reset align to null
    if (
      position === align ||
      (align != undefined && position === POSITION_COMPLEMENTS[align])
    ) {
      iterationAlignments[0] = undefined;
    }
  }

  let bestFit = -Infinity;
  let bestPosition = null;

  for (let i = 0; i < iterationPositions.length; i++) {
    const iterationPosition = iterationPositions[i];
    const screenCoordinates = getScreenCoordinates({
      align: iterationAlignments[i],
      anchorBoundingBox,
      arrowConfig,
      buffer,
      containerBoundingBox,
      offset,
      popoverBoundingBox,
      position: iterationPosition,
    });

    if (screenCoordinates.fit > bestFit) {
      bestFit = screenCoordinates.fit;
      bestPosition = {
        arrow: screenCoordinates.arrow,
        fit: screenCoordinates.fit,
        left: screenCoordinates.left + window.pageXOffset,
        position: iterationPosition,
        top: screenCoordinates.top + window.pageYOffset,
      };

      // If we have already found the ideal fit, use that position
      if (bestFit === 1) {
        break;
      }
    }

    // If we haven't improved the fit, the continue on to try a new position
  }

  return bestPosition;
};

export default findPopoverPosition;
